import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import type { PbiUsageFilters, PbiUsageFilterOptions, PbiUsageWindow, PbiReviewStatus } from '../../types/powerBiUsage';
import { PBI_REVIEW_ORDER, PBI_REVIEW_META } from './status';

interface Props {
    filters: PbiUsageFilters;
    options: PbiUsageFilterOptions | null;
    window: PbiUsageWindow;
    onChange: (f: PbiUsageFilters) => void;
    onWindowChange: (w: PbiUsageWindow) => void;
}

const WINDOW_OPTIONS: Array<{ value: PbiUsageWindow; label: string }> = [
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '180', label: 'Last 180 days' },
    { value: 'all', label: 'All collected' },
];

export function PowerBiUsageFiltersBar({ filters, options, window, onChange, onWindowChange }: Props) {
    // Debounced search: one keystroke must not fire one API call.
    const [searchDraft, setSearchDraft] = useState(filters.search ?? '');
    useEffect(() => { setSearchDraft(filters.search ?? ''); }, [filters.search]);
    useEffect(() => {
        const t = setTimeout(() => {
            const next = searchDraft.trim() || undefined;
            if (next !== filters.search) onChange({ ...filters, search: next });
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDraft]);

    const workspaces = (options?.workspaces ?? []).filter(
        w => filters.includePersonal || w.type !== 'PersonalGroup'
    );

    const toggleReview = (s: PbiReviewStatus) => {
        onChange({ ...filters, reviewStatus: filters.reviewStatus === s ? undefined : s });
    };

    const hasAnyFilter = Boolean(
        filters.workspaceId || filters.search || filters.reviewStatus || filters.usage ||
        filters.includePersonal || filters.includeDeleted
    );

    return (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
            {/* Organisation-level filter first (house rule) */}
            <select
                value={filters.workspaceId ?? ''}
                onChange={e => onChange({ ...filters, workspaceId: e.target.value || undefined })}
                className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700"
            >
                <option value="">All workspaces</option>
                {workspaces.map(w => (
                    <option key={w.workspaceId} value={w.workspaceId}>
                        {w.name} ({w.reportCount})
                    </option>
                ))}
            </select>

            <select
                value={window}
                onChange={e => onWindowChange(e.target.value as PbiUsageWindow)}
                className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700"
            >
                {WINDOW_OPTIONS.map(w => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                ))}
            </select>

            <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    value={searchDraft}
                    onChange={e => setSearchDraft(e.target.value)}
                    placeholder="Search report or workspace…"
                    className="h-9 w-64 rounded-md border border-gray-300 pl-8 pr-2 text-sm text-gray-700"
                />
            </div>

            {/* Review-status pills */}
            <div className="flex items-center gap-1">
                {PBI_REVIEW_ORDER.map(s => {
                    const meta = PBI_REVIEW_META[s];
                    const active = filters.reviewStatus === s;
                    const Icon = meta.icon;
                    return (
                        <button
                            key={s}
                            type="button"
                            onClick={() => toggleReview(s)}
                            aria-pressed={active}
                            className={`inline-flex h-8 items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition ${
                                active ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            <Icon className={`h-3.5 w-3.5 ${meta.iconClass}`} />
                            {meta.label}
                        </button>
                    );
                })}
            </div>

            <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input
                    type="checkbox"
                    checked={filters.includePersonal ?? false}
                    onChange={e => {
                        const checked = e.target.checked;
                        // A selected PERSONAL workspace must not survive hiding personal ones.
                        const selected = options?.workspaces.find(w => w.workspaceId === filters.workspaceId);
                        onChange({
                            ...filters,
                            includePersonal: checked || undefined,
                            workspaceId: !checked && selected?.type === 'PersonalGroup' ? undefined : filters.workspaceId,
                        });
                    }}
                    className="h-3.5 w-3.5 rounded border-gray-300"
                />
                Personal workspaces
            </label>

            <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input
                    type="checkbox"
                    checked={filters.includeDeleted ?? false}
                    onChange={e => onChange({ ...filters, includeDeleted: e.target.checked || undefined })}
                    className="h-3.5 w-3.5 rounded border-gray-300"
                />
                Deleted reports
            </label>

            {hasAnyFilter && (
                <button
                    type="button"
                    onClick={() => onChange({})}
                    className="ml-auto inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                >
                    <X className="h-3.5 w-3.5" /> Clear filters
                </button>
            )}
        </div>
    );
}
